/**
 * VertriebsgebietCardContent Component
 * Displays Vertriebsgebiet (Sales Region) data in a card layout
 */

import { useTranslation } from 'react-i18next';
import mapGermany from '@/assets/map_germany.png';
import type { VertriebsgebietFullData } from '@/types/vertriebsgebiet';

export interface VertriebsgebietCardContentProps {
  data: VertriebsgebietFullData;
}

/**
 * Formats a number with German locale (dot as thousands separator)
 * Returns '-' for zero or undefined values
 */
function formatNumber(value: number | undefined): string {
  if (value === undefined || value === 0) return '-';
  return value.toLocaleString('de-DE');
}

/**
 * Formats currency value with Euro symbol
 * Returns '-' for zero or undefined values
 */
function formatCurrency(value: number | undefined): string {
  if (value === undefined || value === 0) return '-';
  return `${value.toLocaleString('de-DE')} €`;
}

/**
 * Checks if a string value is available (not empty, not "not available")
 */
function isValueAvailable(value: string | undefined): boolean {
  if (!value) return false;
  const lowerValue = value.toLowerCase();
  return lowerValue !== 'not available' && lowerValue !== 'n/a' && value.trim() !== '';
}

/**
 * Formats decimal number with German locale
 * Returns '-' for zero or undefined values
 */
function formatDecimal(value: number | undefined): string {
  if (value === undefined || value === 0) return '-';
  return value.toLocaleString('de-DE', { minimumFractionDigits: 2 });
}

/**
 * Card content for Vertriebsgebiet displaying key statistics and map
 */
export function VertriebsgebietCardContent({
  data,
}: VertriebsgebietCardContentProps) {
  const { t } = useTranslation();
  const { main } = data;

  // Get top 3 milieus from main data, filtering out unavailable ones
  const topMilieus = [
    { name: main.Vertriebsgebiet_Top_1_Millieu_Name, percent: main.Vertriebsgebiet_Top_1_Millieu_Prozent },
    { name: main.Vertriebsgebiet_Top_2_Millieu_Name, percent: main.Vertriebsgebiet_Top_2_Millieu_Prozent },
    { name: main.Vertriebsgebiet_Top_3_Millieu_Name, percent: main.Vertriebsgebiet_Top_3_Millieu_Prozent },
  ].filter(m => isValueAvailable(m.name) && m.percent > 0);

  return (
    <div className="space-y-4">
      {/* Main content - stats and map side by side 50/50 */}
      <div className="grid grid-cols-2 gap-4">
        {/* Left side - Statistics */}
        <div className="space-y-1.5 text-sm">
          <div className="flex justify-between gap-2">
            <span className="text-[var(--color-charcoal)]/70">{t('results.cards.vertriebsgebiet.card.plz')}</span>
            <span className="font-bold text-[var(--color-charcoal)]">
              {main.Vertriebsgebiet_PLZ}
            </span>
          </div>
          {main.Vertriebsgebiet_Anzahl_Einwohner !== undefined && main.Vertriebsgebiet_Anzahl_Einwohner > 0 && (
            <div className="flex justify-between gap-2">
              <span className="text-[var(--color-charcoal)]/70">{t('results.cards.vertriebsgebiet.card.population')}</span>
              <span className="font-bold text-[var(--color-charcoal)]">
                {formatNumber(main.Vertriebsgebiet_Anzahl_Einwohner)}
              </span>
            </div>
          )}
          <div className="flex justify-between gap-2">
            <span className="text-[var(--color-charcoal)]/70">{t('results.cards.vertriebsgebiet.card.households')}</span>
            <span className="font-bold text-[var(--color-charcoal)]">
              {formatNumber(main.Vertriebsgebiet_Anzahl_Haushalte)}
            </span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-[var(--color-charcoal)]/70">{t('results.cards.vertriebsgebiet.card.avgHouseholdSize')}</span>
            <span className="font-bold text-[var(--color-charcoal)]">
              {formatDecimal(main.Vertriebsgebiet_Durchschnittliche_Haushaltsgroesse)}
            </span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-[var(--color-charcoal)]/70">{t('results.cards.vertriebsgebiet.card.purchasingPowerPerCapita')}</span>
            <span className="font-bold text-[var(--color-charcoal)]">
              {formatCurrency(main.Vertriebsgebiet_Kaufkraft_Pro_Einwohner)}
            </span>
          </div>
        </div>

        {/* Right side - Map (centered in its column) */}
        <div className="flex items-center justify-center">
          <img
            src={mapGermany}
            alt={t('results.cards.vertriebsgebiet.card.mapAlt')}
            className="w-full max-w-32 object-contain"
          />
        </div>
      </div>

      {/* Top Milieus section - only show if we have milieu data */}
      {topMilieus.length > 0 && (
        <div className="pt-3 border-t border-[var(--glass-border)]">
          <h4 className="text-sm font-semibold text-[var(--color-charcoal)] mb-3">{t('results.cards.vertriebsgebiet.card.topMilieus')}</h4>
          <div className="flex justify-between gap-2">
            {topMilieus.map((milieu, index) => (
              <div key={index} className="flex-1 text-center">
                <div className="w-8 h-8 mx-auto mb-1 rounded-lg bg-gradient-to-br from-[var(--color-primary-start)]/10 to-[var(--color-primary-end)]/10 flex items-center justify-center">
                  <span className="text-xs font-bold text-[var(--color-primary-start)]">{index + 1}</span>
                </div>
                <p className="text-xs text-[var(--color-charcoal)] truncate" title={milieu.name}>
                  {milieu.name}
                </p>
                <p className="text-xs font-semibold text-[var(--color-primary-start)]">
                  ({milieu.percent}%)
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}

export default VertriebsgebietCardContent;
